import React, { KeyboardEvent, useState } from 'react';
import {
  Accordion,
  ActionItem,
  Avatar,
  Box,
  ColumnDef,
  DataTable,
  Icons,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import formatDate from '@core/utils/formatDate';
import useIsOverflowing from '@app/hooks/useIsOverflowing';
import DeleteSubmissionModal from '../SubmissionActions/DeleteSubmissionModal';
import SubmissionActions from '../SubmissionActions';
import { createStyles } from './SubmissionAccordions.styles';
import {
  ResponsiveGroupedSubmissionGroups,
  ResponsiveSubmissionGroups,
  ResponsiveSubmissionItem,
} from './SubmissionAccordions.types';
import { getSubmissionCapabilities } from './submissionCapabilities';

type Props = {
  accessingAsStaff: boolean;
  expandPopulatedSections?: boolean;
  groupedSubmissionGroups?: ResponsiveGroupedSubmissionGroups;
  hideNoSubmissionIdentities?: boolean;
  isEducator: boolean;
  isGroupAssignment?: boolean;
  onDeleteSubmission?: (
    submission: ResponsiveSubmissionItem
  ) => Promise<void> | void;
  onOpenSubmission?: (submission: ResponsiveSubmissionItem) => void;
  provider: string;
  submissionGroups: ResponsiveSubmissionGroups;
};

type SectionKey = keyof ResponsiveSubmissionGroups;

const StudentName = ({
  name,
  sx,
}: {
  name: string;
  sx: ReturnType<typeof createStyles>['name'];
}) => {
  const [ref, isOverflowing] = useIsOverflowing<HTMLSpanElement>();

  return (
    <Tooltip title={isOverflowing ? name : ''} placement="top" arrow>
      <Box component="span" ref={ref} sx={sx}>
        {name}
      </Box>
    </Tooltip>
  );
};

const sections: Array<{ key: SectionKey; label: string }> = [
  { key: 'earlySubmissions', label: 'On time submissions' },
  { key: 'lateSubmissions', label: 'Late submissions' },
  { key: 'noSubmissions', label: 'No submission' },
];

const capitaliseNamePart = (name?: string) => {
  const trimmedName = name?.trim();
  return trimmedName
    ? `${trimmedName.charAt(0).toUpperCase()}${trimmedName.slice(1)}`
    : '';
};

export const getSubmissionName = ({ user }: ResponsiveSubmissionItem) =>
  [capitaliseNamePart(user.firstName), capitaliseNamePart(user.lastName)]
    .filter(Boolean)
    .join(' ') || 'Unnamed student';

export const getSubmissionInitials = ({ user }: ResponsiveSubmissionItem) => {
  const initials = [user.firstName, user.lastName]
    .filter(Boolean)
    .map((name) => name?.trim().charAt(0))
    .join('')
    .toUpperCase();

  return initials || 'U';
};

const getSubmissionGrade = (submission: ResponsiveSubmissionItem) => {
  if (submission.displayGrade) {
    return submission.displayGrade.includes('NaN')
      ? 'Not graded'
      : submission.displayGrade;
  }

  return submission.receivedAt ? 'Not graded' : '-';
};

const SubmissionAccordions = ({
  accessingAsStaff,
  expandPopulatedSections = false,
  groupedSubmissionGroups,
  hideNoSubmissionIdentities = false,
  isEducator,
  isGroupAssignment = false,
  onDeleteSubmission,
  onOpenSubmission,
  provider,
  submissionGroups,
}: Props) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const styles = createStyles(theme);
  const [submissionPendingDelete, setSubmissionPendingDelete] =
    useState<ResponsiveSubmissionItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const getItemDetails = (submission: ResponsiveSubmissionItem) => {
    const hasSubmission = Boolean(submission.id);
    return {
      capabilities: getSubmissionCapabilities({
        accessingAsStaff,
        hasSubmission,
        isEducator,
        provider,
      }),
      date: submission.receivedAt
        ? formatDate(submission.receivedAt)
        : 'No submission',
      grade: getSubmissionGrade(submission),
      hasSubmission,
      name: getSubmissionName(submission),
    };
  };

  const openSubmission = (submission: ResponsiveSubmissionItem) => {
    if (getItemDetails(submission).capabilities.canOpen) {
      onOpenSubmission?.(submission);
    }
  };

  const handleKeyDown = (
    event: KeyboardEvent,
    submission: ResponsiveSubmissionItem
  ) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    openSubmission(submission);
  };

  const confirmDesktopDelete = async () => {
    if (!submissionPendingDelete || !onDeleteSubmission || deleting) return;
    setDeleting(true);
    try {
      await onDeleteSubmission(submissionPendingDelete);
      setSubmissionPendingDelete(null);
    } catch (_error) {
      // The existing delete operation displays the API error toast. Keep the
      // confirmation open so the educator can retry or cancel.
    } finally {
      setDeleting(false);
    }
  };

  const columns: ColumnDef<ResponsiveSubmissionItem>[] = [
    {
      field: 'user',
      headerName: 'Name',
      width: '50.5%',
      renderCell: (submission) => {
        const name = getSubmissionName(submission);
        return (
          <Box sx={styles.studentIdentity}>
            <Avatar
              altText={name}
              fallbackText={getSubmissionInitials(submission)}
              src={submission.user.avatar}
              sx={styles.avatar}
            />
            <StudentName name={name} sx={styles.name} />
          </Box>
        );
      },
    },
    {
      field: 'receivedAt',
      headerName: 'Submission date',
      width: '30.3%',
      renderCell: (submission) => (
        <Typography sx={styles.rowValue}>
          {getItemDetails(submission).date}
        </Typography>
      ),
    },
    {
      field: 'displayGrade',
      headerName: 'Grade',
      width: '19.2%',
      renderCell: (submission) => (
        <Typography sx={styles.rowValue}>
          {getItemDetails(submission).grade}
        </Typography>
      ),
    },
  ];

  const deleteActions: ActionItem<ResponsiveSubmissionItem>[] = [
    {
      icon: <Icons.DeleteOutlined fontSize="small" />,
      label: 'Delete Submission',
      onClick: setSubmissionPendingDelete,
    },
  ];

  const renderTable = (submissions: ResponsiveSubmissionItem[]) => {
    const canOpenRows = submissions.some(
      (submission) => getItemDetails(submission).capabilities.canOpen
    );
    const canDeleteRows = submissions.some(
      (submission) => getItemDetails(submission).capabilities.canDelete
    );

    return (
      <DataTable
        actionCellStyle={{ width: 56 }}
        actions={deleteActions}
        clickableRows={canOpenRows}
        columnStyles={styles.tableCell}
        columns={columns}
        containerStyle={styles.table}
        getRowId={(submission) => submission.id || submission.user.id}
        headerStyle={styles.tableHeader}
        onRowClick={openSubmission}
        rows={submissions}
        showActions={canDeleteRows}
      />
    );
  };

  const renderCards = (submissions: ResponsiveSubmissionItem[]) => (
    <Box sx={styles.cards}>
      {submissions.map((submission) => {
        const { capabilities, date, grade, hasSubmission, name } =
          getItemDetails(submission);
        return (
          <Box
            aria-label={`${name} submission`}
            component="article"
            key={submission.id || submission.user.id}
            onClick={() => openSubmission(submission)}
            onKeyDown={(event) => handleKeyDown(event, submission)}
            role={capabilities.canOpen ? 'button' : undefined}
            sx={{
              ...styles.card,
              ...(capabilities.canOpen ? styles.clickableCard : {}),
            }}
            tabIndex={capabilities.canOpen ? 0 : undefined}
          >
            <Box sx={styles.cardHeader}>
              <Typography sx={styles.cardName}>{name}</Typography>
              <SubmissionActions
                canDeleteSubmission={capabilities.canDelete}
                hasSubmission={hasSubmission}
                onDeleteSubmission={
                  capabilities.canDelete && onDeleteSubmission
                    ? () => onDeleteSubmission(submission)
                    : undefined
                }
                provider={provider}
                submissionId={submission.id}
                studentName={name}
              />
            </Box>
            <Typography sx={styles.detail}>
              <Box component="span" sx={styles.detailLabel}>
                Submission date:{' '}
              </Box>
              {date}
            </Typography>
            <Typography sx={styles.detail}>
              <Box component="span" sx={styles.detailLabel}>
                Grade:{' '}
              </Box>
              {grade}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  const renderSectionContent = (
    submissions: ResponsiveSubmissionItem[],
    hideIdentities = false
  ) => {
    if (!submissions.length) {
      return (
        <Typography sx={styles.empty}>No students in this category</Typography>
      );
    }

    if (hideIdentities) {
      return (
        <Typography sx={styles.empty}>
          {submissions.length} students have not submitted
        </Typography>
      );
    }

    return isDesktop ? renderTable(submissions) : renderCards(submissions);
  };

  const renderGroupedSection = (key: SectionKey) => {
    const groups = groupedSubmissionGroups?.[key] || [];
    if (!groups.length) {
      return (
        <Typography sx={styles.empty}>No groups in this category</Typography>
      );
    }

    if (key === 'noSubmissions' && hideNoSubmissionIdentities) {
      return (
        <Typography sx={styles.empty}>
          {groups.length} groups have not submitted
        </Typography>
      );
    }

    return (
      <Box sx={styles.nestedGroups}>
        {groups.map((group) => (
          <Accordion
            ariaLabel={`${group.name} (${group.submissions.length})`}
            arrowPosition="left"
            key={group.id || group.name}
            label={`${group.name} (${group.submissions.length})`}
            size="small"
            sx={styles.nestedAccordion}
          >
            {renderSectionContent(group.submissions)}
          </Accordion>
        ))}
      </Box>
    );
  };

  const pendingDeleteName = submissionPendingDelete
    ? getSubmissionName(submissionPendingDelete)
    : '';

  return (
    <>
      <Box sx={styles.wrapper}>
        {sections.map(({ key, label }) => {
          const count = isGroupAssignment
            ? groupedSubmissionGroups?.[key]?.length || 0
            : submissionGroups[key].length;
          return (
            <Accordion
              ariaLabel={`${label} (${count})`}
              arrowPosition="left"
              defaultExpanded={
                expandPopulatedSections ? count > 0 : key === 'earlySubmissions'
              }
              key={`${key}-${expandPopulatedSections ? count : 'default'}`}
              label={`${label} (${count})`}
              size="medium"
              sx={styles.accordion}
            >
              {isGroupAssignment
                ? renderGroupedSection(key)
                : renderSectionContent(
                    submissionGroups[key],
                    key === 'noSubmissions' && hideNoSubmissionIdentities
                  )}
            </Accordion>
          );
        })}
      </Box>

      {submissionPendingDelete && (
        <DeleteSubmissionModal
          deleting={deleting}
          id={`delete-submission-${submissionPendingDelete.id}`}
          onClose={() => !deleting && setSubmissionPendingDelete(null)}
          onConfirm={confirmDesktopDelete}
          open
          studentName={pendingDeleteName}
        />
      )}
    </>
  );
};

export default SubmissionAccordions;
